// src/encounter/services/use-patient-referrals.ts
// [PROD-1395 / PROD-3336] The patient's open referrals to the reader's specialities or role, newest first (Scrips.Patient):
//   GET /api/Patients/{patientId}/referrals?status=sent&status=accepted&status=booked&performerRole=dietitian
//       [&targetSpecialityId={guid}]…   (repeated; with performerRole too the server ORs them, so PROD-1395 rows still come)
// Feeds the "Referral" card on the allied-health pre-encounter. Enabled only with the `referrals` flag on and a
// patient id. A 404 or any error resolves to NO rows: the card then shows nothing (no error card). Nothing is
// logged, so no patient data can reach a log.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { AssessmentReferral } from '@/types/assessment-api';

/** One referral row as the list returns it: the wire row plus the name of the physician who sent it. */
export interface PatientReferralRow extends AssessmentReferral {
  referrerName?: string | null;
}

/** The statuses that still need the dietitian's attention. */
const OPEN_STATUSES = ['sent', 'accepted', 'booked'] as const;

export function patientReferralsPath(patientId: string, performerRole: string | null, specialityIds: readonly string[] = []): string {
  const params = new URLSearchParams();
  for (const s of OPEN_STATUSES) params.append('status', s);
  if (performerRole) params.append('performerRole', performerRole);
  for (const id of specialityIds) params.append('targetSpecialityId', id);
  return `/api/Patients/${encodeURIComponent(patientId)}/referrals?${params.toString()}`;
}

export function usePatientReferrals(
  patientId: string | null | undefined,
  enabled: boolean,
  /** The role to read (PROD-1395), or null for none (PROD-3336: a reader with no dietetics speciality). */
  performerRole: string | null = 'dietitian',
  /** PROD-3336: the reader's own speciality ids (any order; de-duplicated and sorted here). */
  specialityIds: readonly string[] = [],
) {
  const ids = [...new Set(specialityIds.map((x) => x.trim()).filter(Boolean))].sort();
  // Never a request with neither filter: the server would answer with every open referral of the patient.
  const filtered = !!performerRole || ids.length > 0;
  return useQuery<PatientReferralRow[]>({
    queryKey: ['patient-referrals', patientId, performerRole, ids, OPEN_STATUSES],
    enabled: enabled && !!patientId && filtered,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!patientId) return [];
      try {
        const rows = await get<PatientReferralRow[]>(patientReferralsPath(patientId, performerRole, ids), { signal });
        return Array.isArray(rows) ? rows : [];
      } catch {
        return [];
      }
    },
  });
}
